import { useInteraction } from 'Framework/View/Hooks/useInteraction.js'
import type React from 'react'
import { Fragment, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import Actions, { useDispatch, useSelector } from '../../../Flux/index.js'
import { invitationSlice } from '../../../Flux/Slice/Invitation/InvitationSlice.js'
import { hasFeatureAccess, isGlobalAdmin } from '../../../Model/index.js'
import { Environment } from 'environment.js'
import { ChevronDownIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import type { MenuBadgeCounts, MenuItemProps } from './MenuItems.js'
import { isCustomMenuItem, isMenuItemProps, menuItems } from './MenuItems.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { useModal } from '../../Modal/UseModal.js'

export const Menu = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const onNavigate$ = useInteraction<string>()
  const user = useGetAuthenticatedUser()
  const { openModal } = useModal()
  const dispatch = useDispatch()
  const pendingInvites = useSelector((s) => s.invitation.pendingReceived)
  const badgeCounts: MenuBadgeCounts = { pendingInvites }

  useEffect(() => {
    if (user?.id) dispatch(Actions[invitationSlice.name].getAllInvitesRequest())
  }, [user?.id, dispatch])

  const renderLabel = (item: MenuItemProps) => {
    const count = item.badge?.(badgeCounts)
    return (
      <>
        <span>{item.label}</span>
        {!!count && (
          <span className="badge badge-sm badge-primary ml-auto" data-testid="menu-badge" aria-label={`${count} pending`}>
            {count}
          </span>
        )}
      </>
    )
  }

  useEffect(() => {
    const onNavigate$$ = onNavigate$.subscribe((page) => {
      navigate(page)
    })
    return () => onNavigate$$.unsubscribe()
  }, [onNavigate$, navigate])

  const hideSidebar = () => ['', '/', '/register'].includes(location.pathname) || location.pathname.match('email-confirm')

  const isItemEnabled = (item: MenuItemProps): boolean => {
    if (!user) return false
    if (item.requiredFeatureAccess && !hasFeatureAccess(user.org, item.requiredFeatureAccess)) return false
    return true
  }

  const wrapWithModal = (handler: React.MouseEventHandler<HTMLLIElement>) => {
    if (user) {
      return handler
    }

    return () => openModal('signupNudge', {})
  }

  // Divide items
  const tier1Items = menuItems.filter((item) => item.requiredFeatureAccess === undefined || item.requiredFeatureAccess === 'TIER1')
  const tier2Items = menuItems.filter((item) => item.requiredFeatureAccess === 'TIER2')

  return (
    <div className="h-auto">
      {hideSidebar() ? null : (
        <div
          data-testid={'sidebar'}
          id={'tour-sidebar'}
          className="duration-300 px-2 pt-3 pb-2 w-full"
        >
          <ul className="pt-1 overflow-visible">
            {/* TIER1 SECTION */}
            <div id="tour-sidebar-base" className="flex items-center gap-2 mb-2 ml-2">
              <img src="/spcfy-base.png" alt={'Spcfy Base Logo'} className="h-7" />
            </div>

            {tier1Items.map((item, index) => {
              if (isCustomMenuItem(item)) {
                return <Fragment key={index}>{item.item({ onNavigate$: onNavigate$ })}</Fragment>
              } else if (isMenuItemProps(item)) {
                return (
                  <li
                    id={item.id ? item.id : `tier1-${index}`}
                    data-testid={item.testId}
                    key={`tier1-${index}`}
                    className={`
          text-base flex items-center relative gap-x-3 cursor-pointer rounded-md px-2 py-1.5 mb-1 whitespace-nowrap
          ${isItemEnabled(item) ? 'hover:bg-gray-300' : 'text-gray-400 cursor-not-allowed'}
        `}
                    onClick={isItemEnabled(item) ? wrapWithModal(() => onNavigate$.next(item.path!)) : undefined}
                  >
                    {item.icon}
                    {renderLabel(item)}
                  </li>
                )
              }
            })}

            {/* TIER2 HEADER AND SECTION */}
            {tier2Items.length > 0 && (
              <>
                <hr className="my-3 border-t border-gray-300" />
                <div id="tour-sidebar-insight" className="flex items-center gap-2 mb-2 ml-2">
                  <img src={'/spcfy-insight.png'} alt={'Spcfy Insight Logo'} className="h-7" />
                </div>
                {tier2Items.map((item, index) => {
                  if (isCustomMenuItem(item)) {
                    return <Fragment key={index}>{item.item({ onNavigate$: onNavigate$ })}</Fragment>
                  } else if (isMenuItemProps(item)) {
                    return (
                      <TooltipTrigger
                        key={`tier2-${index}`}
                        tooltip={'Requires an active Spcfy INSIGHT license'}
                        disabled={isItemEnabled(item)}
                      >
                        <li
                          id={item.id ? item.id : `tier2-${index}`}
                          data-testid={item.testId}
                          className={`
                      text-base flex items-center relative gap-x-3 cursor-pointer rounded-md px-2 py-1.5 mb-1 whitespace-nowrap
                      ${isItemEnabled(item) ? 'hover:bg-gray-300' : 'text-gray-400 cursor-not-allowed'}
                    `}
                          onClick={isItemEnabled(item) ? wrapWithModal(() => onNavigate$.next(item.path)) : undefined}
                        >
                          {item.icon}
                          {renderLabel(item)}
                        </li>
                      </TooltipTrigger>
                    )
                  }
                })}
              </>
            )}

            {/* ADMIN SECTION */}
            {isGlobalAdmin(user) && (
              <>
                <hr className="my-3 border-t border-gray-300" />
                <details data-testid="sidebar-admin" open={location.pathname.startsWith('/admin')} className="group">
                  <summary className="text-base flex items-center gap-x-3 cursor-pointer hover:bg-gray-300 rounded-md px-2 py-1.5 mb-1 whitespace-nowrap list-none">
                    <ShieldCheckIcon className="w-6 h-6 shrink-0" />
                    <span>Admin</span>
                    <ChevronDownIcon className="w-4 h-4 ml-auto transition-transform group-open:rotate-180" />
                  </summary>
                  <a href={`${Environment.API_URL}/admin/`}>
                    <li className="text-sm flex items-center cursor-pointer hover:bg-gray-300 rounded-md pl-11 pr-2 py-1 mb-1 whitespace-nowrap">
                      Admin View
                    </li>
                  </a>
                  <li
                    className="text-sm flex items-center cursor-pointer hover:bg-gray-300 rounded-md pl-11 pr-2 py-1 mb-1 whitespace-nowrap"
                    onClick={() => navigate('/admin/stats')}
                  >
                    Admin Stats
                  </li>
                  <li
                    className="text-sm flex items-center cursor-pointer hover:bg-gray-300 rounded-md pl-11 pr-2 py-1 mb-1 whitespace-nowrap"
                    onClick={() => navigate('/admin/spin')}
                  >
                    SPIN Metrics
                  </li>
                </details>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
